import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, ShoppingBag } from "lucide-react";
import { formatSGD, waLink } from "../../lib/mockData";
import { useCart } from "../../lib/cartContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { addItem } = useCart();
  const price = product.discountPrice ?? product.price;
  const hasDiscount = !!product.discountPrice;

  const lowStock = product.stock > 0 && product.stock < 6;
  const outOfStock = product.stock === 0;

  const onAdd = (e) => {
    e.preventDefault();
    if (outOfStock) return;
    addItem(product.id, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <article
      className="group bg-white rounded-xl overflow-hidden border border-gold/25 card-lift fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`product-card-${product.id}`}
    >
      {/* object-contain (not cover) so any uploaded ratio — portrait, landscape
          or square — is shown in FULL and never cropped. The cream padding also
          gives the 1.06 hover zoom headroom so it can't clip either. */}
      <Link to={`/product/${product.slug}`} className="block relative aspect-square overflow-hidden bg-cream-2 p-4">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-contain img-zoom"
          loading="lazy"
          decoding="async"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <span className="bg-emerald text-cream text-[10px] px-2 py-1 uppercase tracking-widest font-semibold">
            Halal-Friendly
          </span>
          {product.bestSeller && (
            <span className="bg-gold text-emerald text-[10px] px-2 py-1 uppercase tracking-widest font-semibold">
              Best Seller
            </span>
          )}
        </div>
        {(lowStock || outOfStock) && (
          <div className="absolute top-3 right-3">
            <span
              className={`text-[10px] px-2 py-1 uppercase tracking-widest font-semibold ${
                outOfStock ? "bg-red-600 text-white" : "bg-amber-500 text-white"
              }`}
            >
              {outOfStock ? "Sold Out" : `Only ${product.stock} left`}
            </span>
          </div>
        )}
      </Link>

      <div className="p-5">
        <Link
          to={`/product/${product.slug}`}
          className="block font-serif text-xl leading-tight text-emerald hover:text-gold transition-colors"
        >
          {product.name}
        </Link>
        <p className="text-xs text-emerald-light/80 mt-1 line-clamp-2 min-h-[2rem]">
          {product.shortDesc}
        </p>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-lg font-semibold text-emerald">{formatSGD(price)}</span>
          {hasDiscount && (
            <span className="text-xs text-emerald-light/60 line-through">
              {formatSGD(product.price)}
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            onClick={onAdd}
            disabled={outOfStock}
            className="flex items-center justify-center gap-1.5 bg-emerald text-cream text-xs font-medium py-2.5 hover:bg-emerald-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingBag size={14} strokeWidth={1.7} />
            {outOfStock ? "Sold Out" : "Add to Cart"}
          </button>
          <a
            href={waLink(`Hi HALALGIFHAMPERS, I'd like to enquire about "${product.name}" (${product.sku}).`)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 border border-gold text-emerald text-xs font-medium py-2.5 hover:bg-gold/10 transition-colors"
            data-testid={`product-whatsapp-btn-${product.id}`}
          >
            <MessageCircle size={14} strokeWidth={1.7} />
            Enquire
          </a>
        </div>
      </div>
    </article>
  );
}
